/**
 * Filename / MIME → `FileKind` classification.
 *
 * Single source of truth for renderer dispatch. The registry calls
 * {@link inferFileKind} when a source doesn't declare its own `kind`.
 * Source adapters that already know the kind (artifacts, tool outputs)
 * should set `source.kind` explicitly to bypass this inference.
 */
import type { FileKind } from "../types";

/** File extensions we know how to syntax-highlight. */
const CODE_EXTENSIONS = new Set([
  "js", "jsx", "ts", "tsx", "html", "htm", "css", "json", "py", "python",
  "java", "c", "cpp", "h", "cs", "go", "rs", "php", "rb", "sh",
  "bash", "xml", "yml", "yaml", "toml", "sql", "graphql",
]);

/** File extensions that have a previewable renderer but can't be classified by MIME alone. */
const MARKDOWN_EXTENSIONS = new Set(["md", "markdown"]);
const CSV_EXTENSIONS = new Set(["csv", "tsv"]);
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico"]);
const TEXT_EXTENSIONS = new Set(["txt", "rtf", "log"]);
const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "m4a", "ogg", "flac", "aac"]);
const VIDEO_EXTENSIONS = new Set(["mp4", "webm", "mov", "m4v"]);
/** Word documents — rendered to HTML client-side via mammoth. */
const DOCX_EXTENSIONS = new Set(["docx"]);

/**
 * Binary formats with no in-page renderer — short-circuit to the
 * unsupported branch instead of trying to fetch them as text. `docx` is
 * NOT here anymore — it has a dedicated renderer (mammoth → HTML).
 */
const UNSUPPORTED_EXTENSIONS = new Set([
  "doc", "xls", "xlsx", "ppt", "pptx", "zip", "rar", "7z",
]);

export function getFileExtension(filename: string): string {
  const cleaned = filename.split("?")[0] ?? filename;
  const parts = cleaned.split(".");
  return parts.length > 1 ? (parts[parts.length - 1] ?? "").toLowerCase() : "";
}

/**
 * Classify a source by its filename + optional MIME type. MIME wins when
 * it's specific enough (`application/pdf`, `image/*`); falls back to the
 * extension table otherwise so callers without a MIME header (typical
 * for tool outputs and bare URLs) still get the right renderer.
 */
export function inferFileKind(args: {
  filename: string;
  contentType?: string;
}): FileKind {
  const { filename, contentType } = args;
  const ext = getFileExtension(filename);

  // MIME-first when we have something specific.
  if (contentType) {
    if (contentType === "application/pdf") return "pdf";
    if (contentType.startsWith("image/")) return "image";
    if (contentType.startsWith("audio/")) return "audio";
    if (contentType.startsWith("video/")) return "video";
    if (contentType === "text/html") return "html";
    if (contentType === "text/markdown") return "markdown";
    if (contentType === "text/csv" || contentType === "text/tab-separated-values") {
      return "csv";
    }
  }

  if (ext === "pdf") return "pdf";
  if (IMAGE_EXTENSIONS.has(ext)) return "image";
  if (AUDIO_EXTENSIONS.has(ext)) return "audio";
  if (VIDEO_EXTENSIONS.has(ext)) return "video";
  if (DOCX_EXTENSIONS.has(ext)) return "docx";
  if (MARKDOWN_EXTENSIONS.has(ext)) return "markdown";
  if (CSV_EXTENSIONS.has(ext)) return "csv";
  if (CODE_EXTENSIONS.has(ext)) return "code";
  if (TEXT_EXTENSIONS.has(ext)) return "text";
  if (UNSUPPORTED_EXTENSIONS.has(ext)) return "unsupported";

  // Default to text — `inlineSource` and similar callers may hand us
  // arbitrary text; the code/markdown renderers degrade gracefully if
  // the content isn't actually what their renderer expects.
  return "text";
}
